// CONFIG-FIRST JUSTIFICATION
// --------------------------
// This feedback is fired from inside the imperative drag-and-drop / transition
// handlers that already live in React (the KanbanBoard `alert()` calls on 403 /
// 422 refusals) and rides the existing `kanban*` CustomEvent bus
// (kanbanTransitionRequest, kanbanArchiveTask, kanbanCardUpdated…). Routing it
// through a reactive-json YAML toast would force an unidiomatic event -> RJ-data
// bridge with no existing RJ component to receive it, so a small React toast
// listening on the SAME bus is the minimal, in-pattern choice. No YAML covers it.

import { useEffect, useRef, useState, useCallback } from 'react';
import clsx from 'clsx';
import { X, ShieldAlert, GitBranch, ListChecks, AlertTriangle } from 'lucide-react';

const AUTO_DISMISS_MS = 6000;

// Per-reason presentation. The icon + label carry the meaning so it is NEVER
// conveyed by colour alone (RGAA / WCAG 1.4.1). The accent classes are DS token
// classes from index.css (.alert-*), not arbitrary Tailwind colours.
const REASON_CONFIG = {
  workflow: {
    Icon: GitBranch,
    label: 'Transition refusée',
    alertClass: 'alert-warning',
    assertive: false,
  },
  permission: {
    Icon: ShieldAlert,
    label: 'Permission refusée',
    alertClass: 'alert-danger',
    assertive: true,
  },
  subtasks: {
    Icon: ListChecks,
    label: 'Sous-tâches à terminer',
    alertClass: 'alert-warning',
    assertive: false,
  },
  error: {
    Icon: AlertTriangle,
    label: 'Erreur',
    alertClass: 'alert-danger',
    assertive: true,
  },
};

let _seq = 0;

/**
 * One toast bubble. Owns its own auto-dismiss timer; hovering pauses it.
 */
function Toast({ id, reason, message, onClose }) {
  const cfg = REASON_CONFIG[reason] || REASON_CONFIG.error;
  const { Icon, label, alertClass, assertive } = cfg;
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    if (hovered) return undefined;
    const t = setTimeout(() => onClose(id), AUTO_DISMISS_MS);
    return () => clearTimeout(t);
  }, [hovered, id, onClose]);

  return (
    <div
      className={clsx(
        'alert',
        alertClass,
        'animate-slide-in-right pointer-events-auto items-start shadow-lg',
        'min-w-[18rem] max-w-[26rem]',
      )}
      role={assertive ? 'alert' : 'status'}
      aria-live={assertive ? 'assertive' : 'polite'}
      tabIndex={0}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      // WCAG 2.2.1 (Timing Adjustable): keyboard focus pauses auto-dismiss too,
      // and Échap closes the focused toast.
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
      onKeyDown={(e) => { if (e.key === 'Escape') onClose(id); }}
    >
      <Icon size={18} aria-hidden="true" className="mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="font-semibold leading-snug">{label}</p>
        <p className="mt-0.5 break-words leading-snug">{message}</p>
      </div>
      <button
        type="button"
        onClick={() => onClose(id)}
        aria-label="Fermer la notification"
        className="shrink-0 rounded-md p-0.5 opacity-70 transition hover:opacity-100 focus-visible:opacity-100"
      >
        <X size={16} aria-hidden="true" />
      </button>
    </div>
  );
}

/**
 * Accessible toast host for the Kanban board. Listens on the `kanbanNotify`
 * CustomEvent bus (see kanbanNotify.js / notifyRefusal) and stacks toasts in a
 * fixed, top-right region. Mounted once by KanbanBoard.
 */
export default function KanbanToast() {
  const [toasts, setToasts] = useState([]);
  // Burst de-dup: a single refusal can be emitted twice in the same tick (e.g.
  // the two TaskStatusSelector variants kept mounted by `actions: hide` both
  // watch ~~.taskSyncable.status). Drop an identical {reason|message} arriving
  // within a short window so the user sees one toast, whatever the emitter count.
  const lastRef = useRef({ key: '', t: 0 });

  const close = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  useEffect(() => {
    const handler = (e) => {
      const { reason, message } = e.detail || {};
      if (!message) return;
      const key = `${reason || 'error'}|${message}`;
      const now = Date.now();
      if (key === lastRef.current.key && now - lastRef.current.t < 800) return;
      lastRef.current = { key, t: now };
      _seq += 1;
      const id = _seq;
      setToasts((prev) => [...prev, { id, reason: reason || 'error', message }]);
    };
    document.addEventListener('kanbanNotify', handler);
    return () => document.removeEventListener('kanbanNotify', handler);
  }, []);

  // Host is rendered PERSISTENTLY (even empty): a live region must pre-exist in
  // the DOM before its content is injected, otherwise NVDA/VoiceOver often skip
  // the announcement (WCAG 4.1.3 Status Messages). pointer-events-none keeps the
  // empty host click-through; each toast re-enables pointer-events.
  return (
    <div
      className="pointer-events-none fixed right-4 top-4 z-[60] flex flex-col gap-2"
      role="region"
      aria-label="Notifications du tableau"
    >
      {toasts.map((t) => (
        <Toast
          key={t.id}
          id={t.id}
          reason={t.reason}
          message={t.message}
          onClose={close}
        />
      ))}
    </div>
  );
}
